// public/search-logic.js
// Pure functions for search data processing - no DOM or Firebase dependencies

import { toShortPath } from './url-parser.js';

/**
 * Build a short binder URL from source info.
 * @param {string} source - e.g. 'moxfield'
 * @param {string} sourceType - e.g. 'binder'
 * @param {string} sourceId - e.g. 'abc123'
 * @returns {string|null} - Short URL like '/mfb.abc123' or null
 */
export function buildBinderUrl(source, sourceType, sourceId) {
  return toShortPath({ source, type: sourceType, id: sourceId });
}

/**
 * Extract the correct price from a printing based on finish and currency.
 * @param {Object} printing - Printing object with prices
 * @param {string} [currency] - 'usd' or 'eur' (defaults to 'usd')
 * @returns {number} - Price as float, or 0
 */
export function extractPrice(printing, currency) {
  if (!printing?.prices) return 0;
  const curr = currency || 'usd';
  const finish = printing.finish || (printing.is_foil ? 'foil' : 'nonfoil');

  if (finish === 'etched') {
    return (
      parseFloat(printing.prices[`${curr}_etched`]) ||
      parseFloat(printing.prices[`${curr}_foil`]) ||
      parseFloat(printing.prices[curr]) ||
      0
    );
  }
  if (finish === 'foil') {
    return parseFloat(printing.prices[`${curr}_foil`]) || parseFloat(printing.prices[curr]) || 0;
  }
  return parseFloat(printing.prices[curr]) || 0;
}

/**
 * Regroup API results from card-name-grouped to printing-grouped.
 * Each unique printing = (scryfall_id, finish).
 * @param {Array} nameGroupedResults - API response results grouped by card name
 * @param {string} currency - Currency for price extraction
 * @returns {Array} - Flat array of printing objects, sorted by name then set
 */
export function regroupByPrinting(nameGroupedResults, currency) {
  const printingMap = new Map();

  for (const result of nameGroupedResults) {
    for (const printing of result.printings) {
      const finish = printing.finish || (printing.is_foil ? 'foil' : 'nonfoil');
      const key = `${printing.scryfall_id}|${finish}`;

      if (!printingMap.has(key)) {
        printingMap.set(key, {
          key,
          name: result.name,
          scryfallId: printing.scryfall_id,
          set: printing.set,
          collectorNumber: printing.collector_number,
          rarity: printing.rarity || 'common',
          isFoil: printing.is_foil,
          finish,
          typeLine: printing.type_line,
          manaCost: printing.mana_cost,
          imageData: printing,
          price: extractPrice(printing, currency),
          totalQuantity: 0,
          binders: [],
        });
      }

      const entry = printingMap.get(key);
      entry.totalQuantity += printing.quantity || 1;

      // Parse binderKey format "source:sourceType:sourceId"
      const parts = (printing.binderKey || '').split(':');
      const [source, sourceType, sourceId] = parts.length >= 3 ? parts : [null, null, null];

      // Check if this binder already tracked
      const existingBinder = entry.binders.find((b) => b.binderKey === printing.binderKey);
      if (existingBinder) {
        existingBinder.quantity += printing.quantity || 1;
      } else {
        entry.binders.push({
          binderName: printing.binderName || 'Unknown',
          ownerName: printing.ownerName || null,
          binderKey: printing.binderKey,
          source,
          sourceType,
          sourceId,
          quantity: printing.quantity || 1,
        });
      }
    }
  }

  // Preserve backend sort order (insertion order from Map)
  return Array.from(printingMap.values());
}

/**
 * Sort printing results by the given sort mode.
 * Returns a new sorted array (does not mutate input).
 * @param {Array} printings - Array of printing objects from regroupByPrinting
 * @param {string} sortMode - Sort mode key
 * @returns {Array} - Sorted copy
 */
export function sortPrintings(printings, sortMode) {
  const sorted = [...printings];
  switch (sortMode) {
    case 'name-asc':
      return sorted.sort((a, b) => {
        const n = a.name.localeCompare(b.name);
        return n !== 0 ? n : (a.set || '').localeCompare(b.set || '');
      });
    case 'name-desc':
      return sorted.sort((a, b) => {
        const n = b.name.localeCompare(a.name);
        return n !== 0 ? n : (b.set || '').localeCompare(a.set || '');
      });
    case 'price-desc':
      return sorted.sort((a, b) => b.price - a.price || a.name.localeCompare(b.name));
    case 'price-asc':
      return sorted.sort((a, b) => a.price - b.price || a.name.localeCompare(b.name));
    case 'quantity-desc':
      return sorted.sort(
        (a, b) => b.totalQuantity - a.totalQuantity || a.name.localeCompare(b.name)
      );
    case 'set-desc':
      return sorted.sort((a, b) => (b.set || '').localeCompare(a.set || ''));
    default:
      return sorted;
  }
}

/**
 * Count the number of active (non-empty) filters.
 * @param {Object} filters - Filter state object
 * @returns {number} - Number of active filters
 */
export function countActiveFilters(filters) {
  let count = 0;
  if (filters.type_line) count++;
  if (filters.colors?.length) count++;
  if (filters.rarity) count++;
  if (filters.min_cmc != null || filters.max_cmc != null) count++;
  if (filters.set_codes?.length) count++;
  if (filters.finish) count++;
  if (filters.frames?.length) count++;
  return count;
}

/**
 * Build a clean filters object for the API request, omitting empty values.
 * @param {Object} filters - Raw filter state
 * @returns {Object|null} - Clean filters object or null if no filters active
 */
export function buildApiFilters(filters) {
  const clean = {};
  if (filters.type_line) clean.type_line = filters.type_line;
  if (filters.colors?.length) clean.colors = filters.colors;
  if (filters.rarity) clean.rarity = filters.rarity;
  if (filters.min_cmc != null) clean.min_cmc = filters.min_cmc;
  if (filters.max_cmc != null) clean.max_cmc = filters.max_cmc;
  if (filters.set_codes?.length) clean.set_codes = filters.set_codes;
  if (filters.finish) clean.finish = filters.finish;
  if (filters.frames?.length) clean.frames = filters.frames;
  return Object.keys(clean).length > 0 ? clean : null;
}

// --- URL param helpers ---

/**
 * Parse the `mv` URL param into min/max CMC values.
 * Supports: "3..7" (range), "3.." (min only), "..5" (max only), "4" (exact)
 * @param {string} str - mv param value
 * @returns {{ min_cmc: number|null, max_cmc: number|null }}
 */
export function parseMvParam(str) {
  if (!str || typeof str !== 'string') return { min_cmc: null, max_cmc: null };
  const trimmed = str.trim();

  if (trimmed.includes('..')) {
    const [minStr, maxStr] = trimmed.split('..');
    return {
      min_cmc: minStr !== '' ? parseInt(minStr, 10) : null,
      max_cmc: maxStr !== '' ? parseInt(maxStr, 10) : null,
    };
  }

  // Single number = exact match
  const n = parseInt(trimmed, 10);
  if (!isNaN(n)) return { min_cmc: n, max_cmc: n };

  return { min_cmc: null, max_cmc: null };
}

/**
 * Format min/max CMC values into a `mv` URL param string.
 * @param {number|null} min_cmc
 * @param {number|null} max_cmc
 * @returns {string|null} - e.g. "3..7", "3..", "..5", or null if both null
 */
export function formatMvParam(min_cmc, max_cmc) {
  if (min_cmc == null && max_cmc == null) return null;
  if (min_cmc != null && max_cmc != null && min_cmc === max_cmc) return String(min_cmc);
  const minStr = min_cmc != null ? String(min_cmc) : '';
  const maxStr = max_cmc != null ? String(max_cmc) : '';
  return `${minStr}..${maxStr}`;
}

/**
 * Convert URL route params to filter state object.
 * @param {Object} params - Route params from URL
 * @returns {Object} - Filter state
 */
export function filtersFromParams(params) {
  const mv = parseMvParam(params.mv);
  return {
    type_line: params.type || '',
    colors: params.colors ? params.colors.split(',').filter(Boolean) : [],
    rarity: params.rarity || '',
    min_cmc: mv.min_cmc,
    max_cmc: mv.max_cmc,
    set_codes: params.sets ? params.sets.split(',').filter(Boolean) : [],
    finish: params.finish || '',
    frames: params.frames ? params.frames.split(',').filter(Boolean) : [],
  };
}

/**
 * Convert filter state to URL params object (sparse, omitting defaults).
 * @param {Object} filters - Filter state
 * @returns {Object} - URL params
 */
export function filtersToParams(filters) {
  const p = {};
  if (filters.type_line) p.type = filters.type_line;
  if (filters.colors?.length) p.colors = filters.colors.join(',');
  if (filters.rarity) p.rarity = filters.rarity;
  const mv = formatMvParam(filters.min_cmc, filters.max_cmc);
  if (mv) p.mv = mv;
  if (filters.set_codes?.length) p.sets = filters.set_codes.join(',');
  if (filters.finish) p.finish = filters.finish;
  if (filters.frames?.length) p.frames = filters.frames.join(',');
  return p;
}

/**
 * Map frontend sort mode to backend sort_by parameter.
 * @param {string} sortMode - Frontend sort mode
 * @returns {string} - Backend sort_by value
 */
export function sortModeToSortBy(sortMode) {
  if (sortMode?.startsWith('price')) return 'price';
  if (sortMode?.startsWith('quantity')) return 'quantity';
  if (sortMode?.startsWith('set')) return 'set';
  return 'name';
}
